"use client";

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface HeroBanner {
  desktop: string;
  mobile: string;
  alt: string;
  href: string;
}

function getImageSrc(path: string): string {
  if (!path) return "";
  if (path.startsWith("/")) return path;
  if (path.startsWith("homepage/"))
    return `/api/homepage-images/${path.replace("homepage/", "")}`;
  return path;
}

export function HeroBannerSection({ banners }: { banners: HeroBanner[] }) {
  const n = banners.length;
  // slides: [cloneLast, 0, 1, ..., n-1, cloneFirst]
  const slides =
    n > 1 ? [banners[n - 1], ...banners, banners[0]] : [...banners];

  const trackRef = useRef<HTMLDivElement>(null);
  const indexRef = useRef(n > 1 ? 1 : 0);
  const busyRef = useRef(false);
  const autoRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const [, forceRender] = useState(0);

  const moveTo = useCallback(
    (idx: number, animated: boolean) => {
      const el = trackRef.current;
      if (!el) return;
      if (animated) {
        el.style.transition = "transform 700ms ease-in-out";
      } else {
        el.style.transition = "none";
      }
      el.style.transform = `translateX(-${idx * 100}%)`;
      indexRef.current = idx;
      forceRender((c) => c + 1); // update dots
    },
    []
  );

  const jumpIfNeeded = useCallback(() => {
    const idx = indexRef.current;
    if (idx === 0) {
      // clone-last → real last
      moveTo(n, false);
    } else if (idx === slides.length - 1) {
      // clone-first → real first
      moveTo(1, false);
    }
    busyRef.current = false;
  }, [n, slides.length, moveTo]);

  const handleTransitionEnd = useCallback(() => {
    jumpIfNeeded();
  }, [jumpIfNeeded]);

  const go = useCallback(
    (delta: number) => {
      if (busyRef.current || n <= 1) return;
      busyRef.current = true;
      moveTo(indexRef.current + delta, true);
    },
    [n, moveTo]
  );

  const goToSlide = useCallback(
    (realIdx: number) => {
      if (busyRef.current || n <= 1) return;
      busyRef.current = true;
      moveTo(realIdx + 1, true);
    },
    [n, moveTo]
  );

  // Reset autoplay
  const resetAuto = useCallback(() => {
    if (autoRef.current) clearInterval(autoRef.current);
    if (n <= 1) return;
    autoRef.current = setInterval(() => go(1), 5000);
  }, [n, go]);

  useEffect(() => {
    resetAuto();
    return () => {
      if (autoRef.current) clearInterval(autoRef.current);
    };
  }, [resetAuto]);

  // Set initial position without animation
  useEffect(() => {
    if (n > 1) moveTo(1, false);
  }, [n, moveTo]);

  if (n === 0) return null;

  if (n === 1) {
    const b = banners[0];
    return (
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4">
          <Link href={b.href} className="block rounded-2xl overflow-hidden">
            <Image src={getImageSrc(b.desktop)} alt={b.alt} width={600} height={200} className="hidden md:block w-full h-auto" priority />
            <Image src={getImageSrc(b.mobile)} alt={b.alt} width={600} height={400} className="md:hidden w-full h-auto" priority />
          </Link>
        </div>
      </section>
    );
  }

  const idx = indexRef.current;
  const realIndex =
    idx <= 0 ? n - 1 : idx >= slides.length - 1 ? 0 : idx - 1;

  return (
    <section className="py-6 bg-white">
      <div className="container mx-auto px-4">
        <div className="relative rounded-2xl overflow-hidden">
          <div
            ref={trackRef}
            className="flex"
            style={{ transform: `translateX(-${idx * 100}%)` }}
            onTransitionEnd={handleTransitionEnd}
          >
            {slides.map((banner, i) => (
              <Link key={i} href={banner.href} className="w-full flex-shrink-0 block">
                <Image src={getImageSrc(banner.desktop)} alt={banner.alt} width={600} height={200} className="hidden md:block w-full h-auto" priority={i <= 2} />
                <Image src={getImageSrc(banner.mobile)} alt={banner.alt} width={600} height={400} className="md:hidden w-full h-auto" priority={i <= 2} />
              </Link>
            ))}
          </div>

          <button
            onClick={(e) => { e.preventDefault(); go(-1); resetAuto(); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-colors z-10"
            aria-label="Précédent"
          >
            <ChevronLeft className="w-5 h-5 text-gray-800" />
          </button>
          <button
            onClick={(e) => { e.preventDefault(); go(1); resetAuto(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-colors z-10"
            aria-label="Suivant"
          >
            <ChevronRight className="w-5 h-5 text-gray-800" />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => { goToSlide(i); resetAuto(); }}
                className={`w-3 h-3 rounded-full transition-all ${
                  i === realIndex ? "bg-white scale-110 shadow" : "bg-white/50 hover:bg-white/70"
                }`}
                aria-label={`Bannière ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
